import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { useNavigate } from 'react-router-dom';

function PPMAnalytics() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState('2026-04');
  const [selectedYear, setSelectedYear] = useState('2026');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [stats, setStats] = useState({
    currentPeriod: {
      totalContracts: 0,
      activeContracts: 0,
      recurringRevenue: 0,
      visitsCompleted: 0,
      visitsDue: 0,
      visitsOverdue: 0,
      compliance: 0,
      avgContractValue: 0
    },
    previousPeriod: {
      totalContracts: 0,
      activeContracts: 0,
      recurringRevenue: 0,
      visitsCompleted: 0,
      visitsDue: 0,
      visitsOverdue: 0,
      compliance: 0,
      avgContractValue: 0
    },
    contractBreakdown: {},
    monthlyTrend: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [viewMode, selectedDate, selectedMonth, selectedYear, customStartDate, customEndDate]);

  const getDateRange = () => {
    let currentStart, currentEnd, prevStart, prevEnd, label;
    const today = new Date();

    switch (viewMode) {
      case 'month':
        const [year, month] = selectedMonth.split('-');
        currentStart = new Date(year, month - 1, 1);
        currentEnd = new Date(year, month, 0, 23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setMonth(prevStart.getMonth() - 1);
        prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 1, 0, 23, 59, 59, 999);
        label = currentStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        break;

      case 'quarter':
        const currentQuarter = Math.floor((today.getMonth()) / 3);
        currentStart = new Date(selectedYear, currentQuarter * 3, 1);
        currentEnd = new Date(selectedYear, currentQuarter * 3 + 3, 0, 23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setMonth(prevStart.getMonth() - 3);
        prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 3, 0, 23, 59, 59, 999);
        label = `Q${currentQuarter + 1} ${selectedYear}`;
        break;

      case 'year':
        currentStart = new Date(selectedYear, 0, 1);
        currentEnd = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
        prevStart = new Date(parseInt(selectedYear) - 1, 0, 1);
        prevEnd = new Date(parseInt(selectedYear) - 1, 11, 31, 23, 59, 59, 999);
        label = selectedYear;
        break;

      default:
        currentStart = new Date(selectedYear, 0, 1);
        currentEnd = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
        label = selectedYear;
    }

    return { currentStart, currentEnd, prevStart, prevEnd, label };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      if (!dateRange) {
        setLoading(false);
        return;
      }

      // Placeholder - would fetch from PPM contracts API
      const currentMetrics = {
        totalContracts: 45,
        activeContracts: 42,
        recurringRevenue: 125000,
        visitsCompleted: 158,
        visitsDue: 12,
        visitsOverdue: 3,
        compliance: 94.5,
        avgContractValue: 2976
      };

      const previousMetrics = {
        totalContracts: 43,
        activeContracts: 40,
        recurringRevenue: 118000,
        visitsCompleted: 145,
        visitsDue: 15,
        visitsOverdue: 5,
        compliance: 91.2,
        avgContractValue: 2884
      };

      const contractBreakdown = {
        'Monthly': 25,
        'Quarterly': 12,
        'Bi-Annual': 6,
        'Annual': 2
      };

      const monthlyTrend = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        monthlyTrend.push({
          label: date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
          revenue: 20000 + Math.random() * 5000,
          visits: 25 + Math.floor(Math.random() * 10)
        });
      }

      setStats({
        currentPeriod: currentMetrics,
        previousPeriod: previousMetrics,
        contractBreakdown,
        monthlyTrend
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setLoading(false);
    }
  };

  const getTrendArrow = (current, previous) => {
    if (current > previous) return { icon: '↑', color: 'text-green-600' };
    if (current < previous) return { icon: '↓', color: 'text-red-600' };
    return { icon: '→', color: 'text-blue-600' };
  };

  const ComparisonCard = ({ title, currentValue, previousValue, format = '' }) => {
    const trend = getTrendArrow(currentValue, previousValue);

    return (
      <div className="bg-white rounded-lg shadow p-4 border-t-4 border-pink-500">
        <h3 className="text-pink-600 font-semibold mb-3 text-sm">{title}</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">This Period</span>
            <span className={`font-bold ${trend.color}`}>
              {format}{currentValue.toLocaleString()} {trend.icon}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Last Period</span>
            <span className="font-semibold text-gray-700">{format}{previousValue.toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  };

  const SummaryCard = ({ title, value, format = '' }) => (
    <div className="bg-white rounded-lg shadow p-4 border-t-4 border-pink-500">
      <h3 className="text-pink-600 font-semibold mb-3 text-sm">{title}</h3>
      <div className="text-3xl font-bold text-gray-900">{format}{value.toLocaleString()}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="PPM Analytics"
        subtitle="Preventive Maintenance & Recurring Revenue"
        backButton
        onBack={() => navigate('/dashboard')}
        backTitle="Back to Dashboard"
      />

      {/* Filter Bar */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-full mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600 font-semibold">{getDateRange()?.label || 'Select Date Range'}</div>
            <div className="text-sm text-gray-600">
              Today's Date - {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {['month', 'quarter', 'year'].map(mode => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className={`px-3 py-1 rounded text-sm font-medium transition ${
                    viewMode === mode ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'
                  }`}>
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>

            {viewMode === 'month' && <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />}
            {(viewMode === 'quarter' || viewMode === 'year') && (
              <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg">
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            )}

            <div className="ml-auto text-xs text-gray-500">* All values are excluding VAT</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-6 py-6">
        {loading && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <div className="text-gray-600">Loading analytics data...</div>
          </div>
        )}

        {!loading && (
          <>
        {/* Row 1 - Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard title="Total Contracts" value={stats.currentPeriod.totalContracts} />
          <SummaryCard title="Active Contracts" value={stats.currentPeriod.activeContracts} />
          <SummaryCard title="Annual Recurring Revenue" value={stats.currentPeriod.recurringRevenue} format="£" />
          <SummaryCard title="Avg Contract Value" value={stats.currentPeriod.avgContractValue} format="£" />
        </div>

        {/* Row 2 - Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <ComparisonCard title="Visits Completed" currentValue={stats.currentPeriod.visitsCompleted} previousValue={stats.previousPeriod.visitsCompleted} />
          <ComparisonCard title="Visits Overdue" currentValue={stats.currentPeriod.visitsOverdue} previousValue={stats.previousPeriod.visitsOverdue} />
          <ComparisonCard title="Compliance %" currentValue={stats.currentPeriod.compliance} previousValue={stats.previousPeriod.compliance} format="" />
          <ComparisonCard title="Recurring Revenue" currentValue={stats.currentPeriod.recurringRevenue} previousValue={stats.previousPeriod.recurringRevenue} format="£" />
        </div>

        {/* Row 3 - Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contract Frequency Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Contract Frequency</h3>
            <div className="space-y-3">
              {Object.entries(stats.contractBreakdown).map(([frequency, count]) => {
                const maxCount = Math.max(...Object.values(stats.contractBreakdown));
                return (
                  <div key={frequency}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-gray-700">{frequency}</span>
                      <span className="font-bold text-gray-900">{count} contracts</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-pink-500 h-2 rounded-full" style={{ width: `${(count / maxCount) * 100}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Compliance Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Compliance Status</h3>
            <div className="text-center">
              <div className="text-6xl font-bold text-green-600">{stats.currentPeriod.compliance}%</div>
              <div className="text-sm text-gray-600 mt-2">Compliance Rate</div>
              <div className="mt-6 pt-6 border-t space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Completed</span>
                  <span className="font-bold text-green-600">{stats.currentPeriod.visitsCompleted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Due</span>
                  <span className="font-bold text-orange-600">{stats.currentPeriod.visitsDue}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Overdue</span>
                  <span className="font-bold text-red-600">{stats.currentPeriod.visitsOverdue}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">6-Month Trend</h3>
            <div className="space-y-3">
              {stats.monthlyTrend.map((month, idx) => {
                const maxValue = Math.max(...stats.monthlyTrend.map(m => Math.max(m.visits * 100, m.revenue / 100)), 1);
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">{month.label}</span>
                      <div className="flex gap-3">
                        <span className="text-pink-600 font-semibold">{month.visits} visits</span>
                        <span className="text-green-600 font-semibold">£{month.revenue.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-pink-500 h-2 rounded-full" style={{ width: `${((month.visits * 100) / maxValue) * 100}%` }}></div>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: `${((month.revenue / 100) / maxValue) * 100}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-6 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 bg-pink-500 rounded"></div>
                <span className="text-gray-600">Visits</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span className="text-gray-600">Revenue</span>
              </div>
            </div>
          </div>
        </div>
        </>
        )}
      </main>
    </div>
  );
}

export default PPMAnalytics;
